<template lang="pug">
	//- Оверлей над превью-фреймом во время перетаскивания: подсказывает, что можно разместить на сцене
	//- или на какой текст можно перенести шрифт, и подсвечивает текст, на который шрифт будет применён
	.frame-drop-overlay(:style="overlayStyle")
		.frame-drop-overlay-content
			i.fa(:class="text.icon")
			.frame-drop-overlay-title {{text.title}}
			.frame-drop-overlay-description {{text.description}}
		.frame-drop-overlay-highlight(v-if="highlightStyle" :style="highlightStyle")
</template>

<script>
	import _ 						from "lodash";

	import { Asset } 				from "./../../services/Asset";
	import { Version } 				from "./../../services/Version";
	import { Elements } 			from "./../../services/Elements";
	import { Layout } 				from "./../../services/Layout";

	// Перетаскивание ассетов и файлов во фрейм превью: оверлей над фреймом, приём нагрузки и размещение объекта на сцене.
	//
	// Файл объединяет всю клиентскую часть перетаскивания во фрейм (раньше это были FrameDrop.Frame.js,
	// FrameDrop.Thumbnails.js и FrameDropOverlay.vue):
	// - FrameDropFrame - примесь фрейма превью (Dashboard/Preview.vue): события перетаскивания над фреймом,
	//   показ оверлея и подсветка текста, на который переносится шрифт;
	// - FrameDropThumbnails - примесь миниатюр (список ассетов, вложения ИИ-чата): начало перетаскивания ассета или файлов;
	// - PreviewFrameDrop (default) - компонент оверлея над фреймом, который показывает примесь FrameDropFrame.
	//
	// Расчёты размещения объекта на сцене и создание ассетов из перетащенных файлов - в сервисе Asset.FrameDrop.js.

	// Примесь фрейма превью (Dashboard/Preview.vue): приём ассетов и файлов, перетаскиваемых во фрейм.
	//
	// Во время перетаскивания над превью-фреймом показывается оверлей (компонент PreviewFrameDrop в этом же файле):
	// - для картинок, спрайтовых анимаций, спайнов и видео - подсказка о размещении на сцене,
	// - для шрифтов - подсказка о переносе шрифта на текст, при этом текст под указателем подсвечивается.
	// При сбросе центр объекта ставится в точку сброса, а масштаб подбирается так, чтобы объект не вышел за границы сцены
	// (расчёты размещения - в сервисе Asset.FrameDrop.js).
	const FRAME_DROP_DRAG_SOURCE = "frame-drop-frame";

	export const FrameDropFrame = {

		data() {

			return {
				// Перетаскивание во фрейм началось (смещение мыши не менее 20 пикселей) - показывается оверлей
				frameDropDragActive: false,
				// Указатель находится над фреймом превью
				frameDropOverFrame: false,
				// Нагрузка текущего перетаскивания (ассет креатива или файлы)
				frameDropPayload: null,
				// Текст превью под указателем при перетаскивании шрифта (подсвечивается, шрифт применяется к нему)
				frameDropHighlight: null,
				// Точка зажатия мыши: началом перетаскивания считается смещение от неё минимум на 20 пикселей
				frameDropGestureStart: null,
				// Вложенность наведения на фрейм (dragenter / dragleave приходят и от дочерних элементов)
				frameDropDragCounter: 0
			};

		},

		computed: {

			// Текст оверлея над фреймом (null - оверлей не показывается)
			frameDropOverlayText() {

				if (!this.frameDropDragActive || !this.frameDropPayload) return null;

				return Asset.getFrameDropOverlayText(this.frameDropPayload);

			},

			// Положение оверлея: ровно над превью-фреймом (в координатах дашборда)
			frameDropOverlayStyle() {

				if (!this.frameDropOverlayText) return null;

				let frameRect = this.getFrameDropFrameRect(),
					screenRect = this.$el ? this.$el.getBoundingClientRect() : {top: 0, left: 0};

				return {
					top: `${frameRect.top - screenRect.top}px`,
					left: `${frameRect.left - screenRect.left}px`,
					width: `${frameRect.width}px`,
					height: `${frameRect.height}px`
				};

			},

			// Контур текста, на который будет применён перетаскиваемый шрифт: рисуется дашбордом,
			// когда редактор превью выключен (при включённом редакторе контур текста рисует он сам)
			frameDropHighlightStyle() {

				let highlight = this.frameDropHighlight;

				if (!this.frameDropOverlayText || !highlight?.rect || this.isFrameDropSceneEditorActive()) return null;

				let frameRect = this.getFrameDropFrameRect(),
					screenRect = this.$el ? this.$el.getBoundingClientRect() : {top: 0, left: 0},
					scene = Asset.getFrameDropScene(),
					scaleX = scene.viewportWidth ? frameRect.width / scene.viewportWidth : 1,
					scaleY = scene.viewportHeight ? frameRect.height / scene.viewportHeight : 1;

				return {
					top: `${frameRect.top - screenRect.top + highlight.rect.top * scaleY}px`,
					left: `${frameRect.left - screenRect.left + highlight.rect.left * scaleX}px`,
					width: `${highlight.rect.width * scaleX}px`,
					height: `${highlight.rect.height * scaleY}px`
				};

			}

		},

		watch: {

			// iframe пересоздаётся при смене версии и креатива - обработчики его документа подключаются заново
			frameSrc() {

				this.$nextTick(() => this.bindFrameDropFrameEvents());

			}

		},

		created() {

			// Начало и окончание перетаскивания сообщает миниатюра (см. примесь FrameDropThumbnails в этом же файле)
			Broadcast.on("Frame Drop Started", this.onFrameDropStarted, FRAME_DROP_DRAG_SOURCE);
			Broadcast.on("Frame Drop Finished", this.onFrameDropFinished, FRAME_DROP_DRAG_SOURCE);

		},

		mounted() {

			// Начало перетаскивания - зажатие и смещение мыши как минимум на 20 пикселей (Asset.getFrameDropDragThreshold)
			this.frameDropDocumentMouseDown = e => this.frameDropGestureStart = {x: e.clientX, y: e.clientY};
			this.frameDropDocumentDragEnd = () => this.onFrameDropFinished();

			document.addEventListener("mousedown", this.frameDropDocumentMouseDown, true);
			window.addEventListener("dragend", this.frameDropDocumentDragEnd);
			window.addEventListener("drop", this.frameDropDocumentDragEnd);

			// Файлы из файловой системы можно сбросить прямо над iframe - события перетаскивания приходят в документ превью
			this.bindFrameDropFrameEvents();

		},

		beforeDestroy() {

			document.removeEventListener("mousedown", this.frameDropDocumentMouseDown, true);
			window.removeEventListener("dragend", this.frameDropDocumentDragEnd);
			window.removeEventListener("drop", this.frameDropDocumentDragEnd);

			this.unbindFrameDropFrameEvents();

			Broadcast.off(["Frame Drop Started", "Frame Drop Finished"], FRAME_DROP_DRAG_SOURCE);

		},

		methods: {

			// --- Состояние перетаскивания ---

			onFrameDropStarted(payload) {

				this.frameDropPayload = payload;
				this.frameDropDragActive = true;

			},

			onFrameDropFinished() {

				this.frameDropDragCounter = 0;
				this.frameDropOverFrame = false;
				this.frameDropDragActive = false;
				this.frameDropPayload = null;

				this.clearFrameDropHighlight();

				Asset.clearFrameDropDragPayload();

			},

			// Перетаскивается объект, который можно сбросить во фрейм (ассет креатива или файлы)
			isFrameDropTargetDrag(dataTransfer) {

				return Asset.isFrameDropAssetDrag(dataTransfer) || Asset.isFrameDropFilesDrag(dataTransfer);

			},

			// Перетаскивание началось: зажатие и смещение мыши не менее 20 пикселей от точки нажатия
			// (для файлов из файловой системы точка нажатия мыши дашборду неизвестна)
			isFrameDropDragStarted(e) {

				if (!this.frameDropGestureStart || _.isNil(e?.clientX)) return true;

				let point = this.getFrameDropEventClientPoint(e);

				return Math.hypot(point.clientX - this.frameDropGestureStart.x, point.clientY - this.frameDropGestureStart.y) >= Asset.getFrameDropDragThreshold();

			},

			// --- События перетаскивания над фреймом ---

			onFrameDropDragEnter(e) {

				if (!this.isFrameDropTargetDrag(e.dataTransfer)) return;

				e.preventDefault();

				this.frameDropDragCounter++;
				this.updateFrameDropDragState(e);

			},

			onFrameDropDragOver(e) {

				if (!this.isFrameDropTargetDrag(e.dataTransfer)) return;

				// Без preventDefault браузер не разрешит сброс (файл откроется в новой вкладке)
				e.preventDefault();

				e.dataTransfer.dropEffect = 'copy';

				this.updateFrameDropDragState(e);

			},

			onFrameDropDragLeave(e) {

				if (!this.isFrameDropTargetDrag(e.dataTransfer)) return;

				this.frameDropDragCounter = Math.max(0, this.frameDropDragCounter - 1);

				if (this.frameDropDragCounter) return;

				this.frameDropOverFrame = false;

				// Внутренние перетаскивания (миниатюры дашборда) оверлей сохраняют до конца драга:
				// у файлов из файловой системы dragstart дашборд не получает, поэтому оверлей скрывается при уходе с фрейма
				if (!Asset.isFrameDropAssetDrag(e.dataTransfer)) this.frameDropDragActive = false;

				this.clearFrameDropHighlight();

			},

			async onFrameDropDrop(e) {

				if (!this.isFrameDropTargetDrag(e.dataTransfer)) return;

				e.preventDefault();

				if (!this.isFrameDropDragStarted(e)) return this.onFrameDropFinished();

				// Нагрузку и точку сброса узнаём до сброса состояния перетаскивания.
				// Содержимое файлов доступно только при сбросе - для файлов из файловой системы нагрузка собирается из них
				let payload = e.dataTransfer.files?.length ? Asset.createFrameDropFilesPayload(Array.from(e.dataTransfer.files)) : Asset.readFrameDropPayload(e.dataTransfer),
					clientPoint = this.getFrameDropEventClientPoint(e),
					frameRect = this.getFrameDropFrameRect(),
					element = this.frameDropHighlight?.element || this.getFrameDropTextTarget(this.getFrameDropEventLocalPoint(e))?.element || null;

				this.onFrameDropFinished();

				try {

					// Файлы (файловая система, вложения ИИ-чата): ассеты создаются автоматически, без подтверждения как в ИИ-чате
					if (payload?.kind === 'files') {

						await Asset.importFrameDropFiles(Asset.getFrameDropFiles(payload.storeKey), {frameRect, clientX: clientPoint.clientX, clientY: clientPoint.clientY, element});

					// Шрифт: тексту, на который он сброшен, устанавливается перетащенный шрифт
					} else if (Asset.isFrameDropFontPayload(payload)) {

						Asset.applyFrameDropFont(payload.assetCodename, element);

					// Ассет креатива: масштаб подбирается по размеру самого файла, чтобы объект не вышел за границы сцены
					} else if (payload?.assetCodename) {

						let objectSize = await Asset.getFrameDropObjectSize(payload);

						Asset.placeFrameDropAsset(payload, Asset.computeFrameDropPlacement(frameRect, clientPoint.clientX, clientPoint.clientY, objectSize));

					}

				} finally {

					Asset.releaseFrameDropFiles(payload?.storeKey);

				}

			},

			// Обновление состояния перетаскивания над фреймом: нагрузка, показ оверлея и подсветка текста под указателем
			updateFrameDropDragState(e) {

				if (!this.isFrameDropDragStarted(e)) return;

				let payload = Asset.readFrameDropPayload(e.dataTransfer);

				// Файлы из файловой системы: своей нагрузки у них нет - для подсказки оверлея берём имена файлов из драга
				if (!payload && Asset.isFrameDropFilesDrag(e.dataTransfer)) payload = Asset.createFrameDropDragPreviewPayload(e.dataTransfer);

				if (payload) this.frameDropPayload = payload;

				this.frameDropDragCounter = Math.max(1, this.frameDropDragCounter);
				this.frameDropOverFrame = true;
				// Для файлов из файловой системы перетаскивание началось уже над фреймом (события dragstart дашборд не получает)
				this.frameDropDragActive = true;

				this.updateFrameDropHighlight(e);

			},

			// --- Геометрия фрейма ---

			getFrameDropFrameElement() {

				return this.$refs.iframe || null;

			},

			getFrameDropFrameWindow() {

				return this.getFrameDropFrameElement()?.contentWindow || null;

			},

			// Документ превью (предпросмотр открыт с того же домена, но доступ обёрнут - чужой домен бросил бы исключение)
			getFrameDropFrameDocument() {

				try {

					return this.getFrameDropFrameWindow()?.document || null;

				} catch (e) {

					return null;

				}

			},

			getFrameDropFrameRect() {

				let frameElement = this.getFrameDropFrameElement();

				return frameElement ? frameElement.getBoundingClientRect() : {top: 0, left: 0, width: 0, height: 0};

			},

			// Точка события перетаскивания в координатах дашборда: события документа превью (iframe того же домена)
			// переводятся в координаты экрана дашборда
			getFrameDropEventClientPoint(e) {

				let frameWindow = this.getFrameDropFrameWindow();

				if (frameWindow && e?.view && e.view !== window) {

					let frameRect = this.getFrameDropFrameRect(),
						scaleX = frameRect.width / (e.view.innerWidth || 1),
						scaleY = frameRect.height / (e.view.innerHeight || 1);

					return {clientX: frameRect.left + e.clientX * scaleX, clientY: frameRect.top + e.clientY * scaleY};

				}

				return {clientX: e?.clientX || 0, clientY: e?.clientY || 0};

			},

			// Точка события перетаскивания в координатах кадра превью (в этих координатах задаются элементы превью)
			getFrameDropEventLocalPoint(e) {

				let frameWindow = this.getFrameDropFrameWindow();

				if (frameWindow && e?.view && e.view !== window) return {x: e.clientX, y: e.clientY};

				return Asset.getFrameDropFramePoint(this.getFrameDropFrameRect(), Asset.getFrameDropScene(), e?.clientX, e?.clientY);

			},

			// --- Подсветка текста при перетаскивании шрифта ---

			// Шрифт применяется к тексту, на который он сброшен, поэтому текст под указателем подсвечивается
			// так же, как элементы в редакторе: контуром самого редактора превью (если он включён)
			// либо контуром дашборда над фреймом (см. компонент PreviewFrameDrop в этом же файле)
			updateFrameDropHighlight(e) {

				if (!Asset.isFrameDropFontPayload(this.frameDropPayload)) return this.clearFrameDropHighlight();

				let target = this.getFrameDropTextTarget(this.getFrameDropEventLocalPoint(e));

				if (!target) return this.clearFrameDropHighlight();

				// Указатель остаётся над тем же текстом - подсветка не меняется
				if (target.elementCodename === this.frameDropHighlight?.elementCodename) return;

				this.frameDropHighlight = target;

				// Подсветку текста в превью рисует редактор превью - как при наведении строки в дереве элементов
				if (this.isFrameDropSceneEditorActive()) Broadcast.call("Send To iFrame", [{HitElement: [target.screenCodename, target.elementCodename]}]);

			},

			clearFrameDropHighlight() {

				if (!this.frameDropHighlight) return;

				// Контур редактора превью нужно снять, если он был показан для подсвеченного текста
				let highlightedByEditor = this.isFrameDropSceneEditorActive(),
					screenCodename = this.frameDropHighlight.screenCodename;

				this.frameDropHighlight = null;

				if (highlightedByEditor) Broadcast.call("Send To iFrame", [{HitElement: [screenCodename, '']}]);

			},

			// Редактор превью включён (открыта вкладка редактора элементов) - подсветку текста рисует он
			isFrameDropSceneEditorActive() {

				return !!(Layout.asideOpened && Layout.settingsTabName === 'elements');

			},

			// Текстовый элемент превью под указанной точкой (в координатах кадра)
			getFrameDropTextTarget(point) {

				let element = _.find(this.getFrameDropElementsAtPoint(this.getFrameDropFrameWindow(), point), {type: 'text'});

				if (!element) return null;

				return {
					screenCodename: element['screen_codename'],
					elementCodename: element.codename,
					// Элемент версии из дашборда: из него берётся сообщение локализации, которому задаётся шрифт
					element: this.getFrameDropVersionElement(element),
					// Прямоугольник текста в координатах кадра (у Pixi рендерера DOM-узла нет - подсветку рисует редактор превью)
					rect: this.getFrameDropElementFrameRect(element)
				};

			},

			// Элементы превью под точкой: у HTML рендерера элементы - DOM-узлы с привязанными данными,
			// у Pixi рендерера попадание по точке проверяет редактор превью (как в Editor.getImportantElementAtPoint)
			getFrameDropElementsAtPoint(frameWindow, point) {

				if (!frameWindow || _.isNil(point?.x)) return [];

				let pixiEditor = frameWindow.PixiRenderer?.Editor;

				if (pixiEditor?.getElementsAtPoint) return pixiEditor.getElementsAtPoint(point.x, point.y);

				let elementDocument = this.getFrameDropFrameDocument();

				if (!elementDocument?.elementsFromPoint) return [];

				return _.compact(_.map(elementDocument.elementsFromPoint(point.x, point.y), target => {

					while (target && !target.elementProps) target = target.parentElement;

					return target?.elementProps || null;

				}));

			},

			// Элемент версии из дашборда по элементу превью (нужен, например, для codename сообщения локализации текста)
			getFrameDropVersionElement(frameElement) {

				let screenElements = Elements.getVersionElementsByScreen(Version.SelectedVersion)?.[frameElement?.['screen_codename']];

				return screenElements?.[frameElement?.codename] || null;

			},

			// Прямоугольник элемента превью в координатах кадра (null - у элемента нет DOM-узла)
			getFrameDropElementFrameRect(frameElement) {

				let element = frameElement?.el;

				if (!element?.getBoundingClientRect) return null;

				let rect = element.getBoundingClientRect();

				return {top: rect.top, left: rect.left, width: rect.width, height: rect.height};

			},

			// --- Обработчики документа превью ---

			// Файлы из файловой системы можно сбросить прямо над iframe, тогда события перетаскивания приходят
			// в документ превью - поэтому те же обработчики подключаются и к нему (предпросмотр открыт с того же домена)
			bindFrameDropFrameEvents() {

				let frameDocument = this.getFrameDropFrameDocument();

				if (!frameDocument) return;

				frameDocument.addEventListener("dragenter", this.onFrameDropDragEnter);
				frameDocument.addEventListener("dragover", this.onFrameDropDragOver);
				frameDocument.addEventListener("dragleave", this.onFrameDropDragLeave);
				frameDocument.addEventListener("drop", this.onFrameDropDrop);

			},

			unbindFrameDropFrameEvents() {

				let frameDocument = this.getFrameDropFrameDocument();

				if (!frameDocument) return;

				frameDocument.removeEventListener("dragenter", this.onFrameDropDragEnter);
				frameDocument.removeEventListener("dragover", this.onFrameDropDragOver);
				frameDocument.removeEventListener("dragleave", this.onFrameDropDragLeave);
				frameDocument.removeEventListener("drop", this.onFrameDropDrop);

			}

		}

	};

	// Примесь FrameDropThumbnails: для мест, где показываются миниатюры ассетов креатива и вложений ИИ-чата (список ассетов, сообщения чата).
	// Позволяет перетаскивать миниатюру во фрейм превью: ассет - как готовый объект сцены (или шрифт для текста),
	// файлы - как будущие ассеты (они будут созданы автоматически при сбросе во фрейм, без подтверждения как в ИИ-чате).
	//
	// Пример использования в шаблоне:
	//   img(:draggable="isFrameDropDraggable(asset)" v-on:dragstart="startFrameDropAssetDrag($event, asset)" v-on:dragend="finishFrameDropDrag()")
	export const FrameDropThumbnails = {

		methods: {

			// Полезная нагрузка перетаскивания для ассета (null - ассет нельзя разместить на сцене)
			getFrameDropAssetPayload(asset) {

				return Asset.createFrameDropAssetPayload(asset);

			},

			// Миниатюра ассета перетаскивается во фрейм (draggable)
			isFrameDropDraggable(asset) {

				return !!this.getFrameDropAssetPayload(asset);

			},

			// Начало перетаскивания ассета креатива: тип элемента (картинка, спрайтовая анимация, спайн, видео)
			// или шрифт, который применяется к тексту под указателем
			startFrameDropAssetDrag(event, asset) {

				let payload = this.getFrameDropAssetPayload(asset);

				if (!payload) {

					event.preventDefault();

					return;

				}

				this.startFrameDropDrag(event, payload);

			},

			// Файлы для перетаскивания из разобранных вложений ИИ-чата: у вложения есть blob и имя файла,
			// а загружать новые ассеты нужно файлами с именем (по имени сервер задаёт codename ассета)
			createFrameDropFiles(entries) {

				// Части ассета Spine приходят объектом ({json, atlas, png}), остальные вложения - списком
				let list = Array.isArray(entries) ? entries : Object.values(entries || {});

				return list.map(entry => new File([entry.blob], entry.name, {type: entry.blob?.type || 'application/octet-stream'}));

			},

			// Начало перетаскивания файлов (вложения ИИ-чата, которые ещё не стали ассетами)
			startFrameDropFilesDrag(event, files) {

				let payload = Asset.createFrameDropFilesPayload(files);

				if (!payload) {

					event.preventDefault();

					return;

				}

				this.startFrameDropDrag(event, payload);

			},

			startFrameDropDrag(event, payload) {

				Asset.writeFrameDropPayload(event.dataTransfer, payload);

				let startPoint = {x: event.clientX, y: event.clientY},
					threshold = Asset.getFrameDropDragThreshold(),
					started = false,
					element = event.currentTarget;

				let onDrag = dragEvent => {

					if (started) return;
					if (Math.hypot(dragEvent.clientX - startPoint.x, dragEvent.clientY - startPoint.y) < threshold) return;

					started = true;

					// Началом перетаскивания считается зажатие и смещение мыши не менее чем на 20 пикселей -
					// до этого оверлей над фреймом не показывается.
					// Фрейм превью узнаёт о начале перетаскивания заранее: во время драга события сброса
					// над iframe приходят в документ превью, поэтому фрейм отключает у iframe перехват мыши
					Broadcast.call("Frame Drop Started", [payload]);

				};

				let onDragEnd = () => {

					element.removeEventListener("drag", onDrag);
					element.removeEventListener("dragend", onDragEnd);

					Broadcast.call("Frame Drop Finished");

				};

				// Смещение указателя во время драга приходит событиями drag на перетаскиваемом элементе
				element.addEventListener("drag", onDrag);
				element.addEventListener("dragend", onDragEnd);

			},

			// Перетаскивание закончилось (сброс или отмена): освобождаем нагрузку и скрываем оверлей во фрейме
			finishFrameDropDrag() {

				Broadcast.call("Frame Drop Finished");

			}

		}

	};

	// Компонент оверлея: текст и его положение приходят из примеси FrameDropFrame (см. выше),
	// сама примесь считает текст по перетаскиваемой нагрузке (Asset.getFrameDropOverlayText)
	export default {
		name: "PreviewFrameDrop",
		props: {
			// {icon, title, description} - подсказка для текущей перетаскиваемой нагрузки
			text: {
				type: Object,
				required: true
			},
			// Положение оверлея над превью-фреймом (в координатах экрана дашборда)
			overlayStyle: {
				type: Object,
				default: () => ({})
			},
			// Положение подсвечиваемого текста (синий контур, как при наведении элементов в редакторе)
			highlightStyle: {
				type: Object,
				default: null
			}
		}
	};
</script>

<style lang="sass">
	// Слой перетаскивания над превью-фреймом: не перехватывает мышь, чтобы драг и сброс доходили до фрейма
	.frame-drop-overlay
		position: absolute
		z-index: 250
		display: flex
		align-items: center
		justify-content: center
		padding: 15px
		box-sizing: border-box
		pointer-events: none
		background-color: rgba(35,40,44,0.55)
		backdrop-filter: blur(6px)
		-webkit-backdrop-filter: blur(6px)
		border: 2px dashed #20a8d8
		overflow: hidden

		.frame-drop-overlay-content
			display: flex
			flex-direction: column
			align-items: center
			gap: 8px
			max-width: 520px
			text-align: center
			color: #f3f4f5

			& > i.fa
				font-size: 36px
				color: #20a8d8

			.frame-drop-overlay-title
				font-size: 15px
				font-weight: 600

			.frame-drop-overlay-description
				font-size: 12px
				line-height: 17px
				color: #adb6bf

		// Подсветка текста, на который будет применён перетаскиваемый шрифт
		.frame-drop-overlay-highlight
			position: absolute
			border: 2px solid #20a8d8
			box-shadow: 0 0 4px rgba(32, 168, 216, 0.65)
</style>
